/*
  LOKT a11y r3 (2026-09-24, implementation plan Wave 1). One cached, deferred file for
  the keyboard and ARIA fixes that the vendor bundle (assets/shrine-main.js, verbatim,
  never hand-edited) cannot take. Loaded once from layout/theme.liquid (body end).

  1. STICKY CHROME NEVER HIDES FOCUS (WCAG 2.4.11, W3C technique C43).
     The sticky header and the fixed bottom bars (sticky add to cart, age notice, any
     element marked data-lokt-sticky="top|bottom") are measured with a ResizeObserver
     and written to <html> as scroll-padding-top / -bottom, so every browser-initiated
     scroll (Tab, Shift+Tab, #anchor links, scrollIntoView) stops clear of them. A
     keyboard focusin check then nudges the page for the one case scroll-padding cannot
     see: a bar that appears after the scroll (the sticky ATC slides in once the main
     button leaves the viewport). Mouse and touch focus are never moved.

  2. MENU DRAWER FOCUS LOOP. Shrine traps focus to the whole <details>, so the hidden
     "Menu" summary under the open drawer was one stop in every lap (Tab and Shift+Tab).
     While the drawer is open, Tab and Shift+Tab now cycle through the visible controls
     of the open panel only (the drawer, or the open slide-over submenu). Escape and the
     close buttons stay the vendor's (focus returns to the Menu button).

  3. DANGLING ARIA REFERENCES (idref resolver on live, 2026-09-24):
       aria-owns / aria-controls="predictive-search-results" on every search input,
         while the results list only exists after a query. aria-owns is dropped (the
         combobox pattern uses aria-controls), aria-controls is set only while
         #predictive-search-results exists;
       aria-describedby="Thumbnail-..." on gallery thumbnails: the <img> id carries a
         trailing newline from a Liquid capture, so it never matched. The id is trimmed
         (source fix handed to the gallery owner: | strip on thumbnail_id);
       aria-labelledby="... Badge-<section>-<id>" on product cards whose badge did not
         render: the missing id is removed from the list, the card link keeps its name.
     A debounced MutationObserver re-runs the sweep for search results, facet filtering
     and quick-add, which re-render those nodes.

  4. RAILS ARE TAB STOPS ONLY WHILE THEY SCROLL. A horizontally scrolling rail needs a
     tab stop so keyboard users can scroll it (axe scrollable-region-focusable), but the
     same rail laid out as a grid (desktop) became an invisible stop that does nothing.
     Each rail keeps tabindex="0" only while its content overflows.

  No dependencies, no globals except window.LoktA11y (for QA). Every block is guarded, so
  a failure in one never stops the others.
*/
(function () {
  'use strict';
  if (window.LoktA11y) return;
  var root = document.documentElement;
  var raf = window.requestAnimationFrame || function (fn) { return setTimeout(fn, 16); };

  function isShown(el) {
    if (!el || !el.isConnected) return false;
    if (typeof el.checkVisibility === 'function') {
      if (!el.checkVisibility({ checkOpacity: true, checkVisibilityCSS: true })) return false;
    } else {
      var cs = getComputedStyle(el);
      if (cs.display === 'none' || cs.visibility !== 'visible' || cs.opacity === '0') return false;
    }
    var r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0;
  }

  /* ---------- 1. sticky chrome vs focus ---------- */
  var STICKY_SEL = '.section-header, sticky-atc, .lk-buy__sticky, #lokt-agegate, [data-lokt-sticky]';
  var edge = { top: 0, bottom: 0 };

  function measure() {
    var top = 0;
    var bottom = 0;
    var vh = window.innerHeight;
    var bars = document.querySelectorAll(STICKY_SEL);
    for (var i = 0; i < bars.length; i++) {
      var el = bars[i];
      var cs = getComputedStyle(el);
      if (cs.display === 'none' || cs.visibility === 'hidden') continue;
      var h = el.getBoundingClientRect().height;
      if (h <= 0 || h > vh * 0.5) continue;
      var want = el.getAttribute('data-lokt-sticky');
      var t = parseFloat(cs.top);
      var b = parseFloat(cs.bottom);
      if (want === 'bottom' || (cs.position === 'fixed' && !isNaN(b) && b < vh / 2 && (isNaN(t) || t > vh / 2 || cs.top === 'auto'))) {
        /* Bottom bars are reserved whenever they can show, not only while on screen:
           the sticky ATC slides in after the scroll that a focus move causes. A bar that
           is faded out (age notice before it opens) is skipped. */
        if (cs.opacity === '0') continue;
        /* The age strip lifts above a docked buy bar by transform, not by bottom
           (snippets/lokt-agegate-bar.liquid, QA STRIP-CLS-PRE), so its lift is read
           from --lokt-agegate-lift; every other bar has no such variable (0). */
        var lift = parseFloat(cs.getPropertyValue('--lokt-agegate-lift')) || 0;
        bottom = Math.max(bottom, h + (isNaN(b) ? 0 : Math.max(0, b)) + Math.max(0, lift));
      } else if (want === 'top' || cs.position === 'sticky' || (cs.position === 'fixed' && !isNaN(t) && t < vh / 2)) {
        /* A sticky header hides on scroll down and returns on scroll up (Shrine
           "on scroll up" type), so its height is reserved while it is sticky. */
        top = Math.max(top, h + (isNaN(t) ? 0 : Math.max(0, t)));
      }
    }
    top = Math.round(top);
    bottom = Math.round(bottom);
    if (top !== edge.top) root.style.scrollPaddingTop = top ? top + 8 + 'px' : '';
    if (bottom !== edge.bottom) root.style.scrollPaddingBottom = bottom ? bottom + 8 + 'px' : '';
    edge.top = top;
    edge.bottom = bottom;
  }

  /* A bar can dock AFTER a keyboard focus move has scrolled: the Tab scroll is smooth,
     and the buy box's sticky bar (and the age strip lifting above it) only appears once
     that scroll passes the in-page Add. Whenever the reserved edges grow, the element a
     keyboard user focused in the last few seconds is checked again (2026-09-27 QA
     OV-QA-2). Mouse and touch focus never move the page. */
  var lastKey = { el: null, t: 0 };
  var RECHECK_MS = 2500;
  function recheckFocus() {
    var el = lastKey.el;
    if (!el || document.activeElement !== el || Date.now() - lastKey.t > RECHECK_MS) return;
    try { if (!el.matches(':focus-visible')) return; } catch (err) { return; }
    nudge(el);
  }

  var measureQueued = false;
  function queueMeasure() {
    if (measureQueued) return;
    measureQueued = true;
    raf(function () {
      measureQueued = false;
      var before = edge.top + edge.bottom;
      measure();
      if (edge.top + edge.bottom > before) recheckFocus();
    });
  }

  function watchSticky() {
    var ro = window.ResizeObserver ? new ResizeObserver(queueMeasure) : null;
    var mo = window.MutationObserver ? new MutationObserver(queueMeasure) : null;
    var seen = [];
    function bind() {
      var bars = document.querySelectorAll(STICKY_SEL);
      for (var i = 0; i < bars.length; i++) {
        if (seen.indexOf(bars[i]) !== -1) continue;
        seen.push(bars[i]);
        if (ro) ro.observe(bars[i]);
        /* the sticky ATC and the age notice show and hide by class / data-* flips */
        if (mo) mo.observe(bars[i], { attributes: true, attributeFilter: ['class', 'style', 'hidden', 'data-shown', 'aria-hidden'] });
      }
      queueMeasure();
    }
    bind();
    window.addEventListener('resize', queueMeasure, { passive: true });
    /* the buy box announces its bar (assets/lokt-launch-buybox.js) */
    document.addEventListener('lokt:sticky-change', queueMeasure);
    window.addEventListener('load', bind);
    /* the age notice is built by script after load */
    setTimeout(bind, 2500);
    document.addEventListener('shopify:section:load', bind);
  }

  var FOCUS_SKIP = '.section-header, sticky-atc, .lk-buy__sticky, #lokt-agegate, [data-lokt-sticky], .drawer, cart-drawer, .menu-drawer, [role="dialog"], [aria-modal="true"], dialog, details-modal, modal-dialog, quick-add-modal, .search-modal, promo-popup, product-modal';

  // Focus inside any position:fixed/sticky layer (a cookie banner, an app widget not in
  // FOCUS_SKIP) moves with the viewport, so scrolling the page never reveals it.
  function inFixedLayer(el) {
    for (var n = el; n && n !== document.body && n.nodeType === 1; n = n.parentElement) {
      var p = getComputedStyle(n).position;
      if (p === 'fixed' || p === 'sticky') return true;
    }
    return false;
  }

  /* A radio or checkbox inside a card (the buy box packs) is judged by its card, so the
     whole choice clears the bars, not only the dot. */
  function focusRect(el) {
    if (el.tagName === 'INPUT' && (el.type === 'radio' || el.type === 'checkbox')) {
      var lab = el.closest('label');
      if (lab) return lab.getBoundingClientRect();
    }
    return el.getBoundingClientRect();
  }

  function nudge(el) {
    var r = focusRect(el);
    var topLine = edge.top + 8;
    var bottomLine = window.innerHeight - edge.bottom - 8;
    if (r.top < topLine) {
      window.scrollBy(0, r.top - topLine);
    } else if (r.bottom > bottomLine && r.height < bottomLine - topLine) {
      window.scrollBy(0, r.bottom - bottomLine);
    }
  }

  function unobscure(e) {
    var el = e.target;
    if (!el || el.nodeType !== 1 || el === document.body) return;
    try { if (!el.matches(':focus-visible')) return; } catch (err) { return; }
    if (el.closest(FOCUS_SKIP) || inFixedLayer(el)) return;
    lastKey.el = el;
    lastKey.t = Date.now();
    raf(function () {
      if (document.activeElement !== el) return;
      measure();
      nudge(el);
    });
  }

  /* ---------- 2. menu drawer focus loop ---------- */
  var FOCUSABLE = 'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, iframe, [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

  function drawerPanel(drawer) {
    /* The open slide-over submenu wins over the drawer; an accordion-style submenu
       expands in place, so the drawer stays the panel. */
    var subs = drawer.querySelectorAll('.menu-drawer__submenu');
    var panel = null;
    for (var i = 0; i < subs.length; i++) {
      var prev = subs[i].previousElementSibling;
      var host = subs[i].parentElement;
      var open = (prev && prev.tagName === 'DETAILS' && prev.open) || (host && host.tagName === 'DETAILS' && host.open);
      if (!open) continue;
      var pos = getComputedStyle(subs[i]).position;
      if ((pos === 'absolute' || pos === 'fixed') && isShown(subs[i])) panel = subs[i];
    }
    return panel || drawer;
  }

  function focusablesIn(panel) {
    var all = panel.querySelectorAll(FOCUSABLE);
    var out = [];
    for (var i = 0; i < all.length; i++) {
      var el = all[i];
      if (el.tabIndex < 0) continue;
      /* skip controls that belong to a closed or covered nested submenu */
      var sub = el.parentElement && el.parentElement.closest('.menu-drawer__submenu');
      if (sub && sub !== panel && panel.contains(sub)) {
        var p = sub.previousElementSibling;
        var h = sub.parentElement;
        var subOpen = (p && p.tagName === 'DETAILS' && p.open) || (h && h.tagName === 'DETAILS' && h.open);
        if (!subOpen) continue;
      }
      if (isShown(el)) out.push(el);
    }
    return out;
  }

  function trapDrawer(e) {
    if (e.key !== 'Tab' || e.altKey || e.ctrlKey || e.metaKey) return;
    var details = document.getElementById('Details-menu-drawer-container');
    var drawer = document.getElementById('menu-drawer');
    if (!details || !drawer || !details.open) return;
    if (!isShown(drawer)) return;
    var panel = drawerPanel(drawer);
    var list = focusablesIn(panel);
    if (!list.length) return;
    var first = list[0];
    var last = list[list.length - 1];
    var active = document.activeElement;
    var inside = panel.contains(active) && list.indexOf(active) !== -1;
    var target = null;
    if (!inside) target = e.shiftKey ? last : first;
    else if (!e.shiftKey && active === last) target = first;
    else if (e.shiftKey && active === first) target = last;
    if (!target) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    target.focus();
  }

  /* ---------- 3. dangling ARIA references ---------- */
  function sweepAria() {
    var i, el;
    var results = document.getElementById('predictive-search-results');
    var inputs = document.querySelectorAll('[aria-owns="predictive-search-results"], [aria-controls="predictive-search-results"], input[data-lokt-ps-controls]');
    for (i = 0; i < inputs.length; i++) {
      el = inputs[i];
      if (el.hasAttribute('aria-owns')) el.removeAttribute('aria-owns');
      if (!el.hasAttribute('data-lokt-ps-controls')) el.setAttribute('data-lokt-ps-controls', '');
      if (results) {
        if (el.getAttribute('aria-controls') !== 'predictive-search-results') el.setAttribute('aria-controls', 'predictive-search-results');
      } else if (el.hasAttribute('aria-controls')) {
        el.removeAttribute('aria-controls');
      }
    }

    var imgs = document.querySelectorAll('img[id^="Thumbnail-"]');
    for (i = 0; i < imgs.length; i++) {
      var id = imgs[i].id;
      var trimmed = id.trim();
      if (trimmed !== id && !document.getElementById(trimmed)) imgs[i].id = trimmed;
    }

    var refs = document.querySelectorAll('[aria-labelledby*="Badge-"], [aria-describedby^="Thumbnail-"]');
    for (i = 0; i < refs.length; i++) {
      el = refs[i];
      ['aria-labelledby', 'aria-describedby'].forEach(function (attr) {
        var v = el.getAttribute(attr);
        if (!v) return;
        var ids = v.split(/\s+/).filter(Boolean);
        var keep = ids.filter(function (x) { return !/^(NoMediaStandardBadge|Badge|Thumbnail)-/.test(x) || document.getElementById(x); });
        if (keep.length === ids.length) return;
        if (keep.length) el.setAttribute(attr, keep.join(' '));
        else el.removeAttribute(attr);
      });
    }
  }

  /* ---------- 4. rails: tab stop only while scrollable ---------- */
  var RAIL_SEL = '.lokt-trev__rail, .lokt-wl__index, .lokt-home__cards, .lokt-mix-tblwrap, .product__media-list[aria-label], [data-lokt-rail]';
  var railRO = window.ResizeObserver ? new ResizeObserver(function (entries) {
    for (var i = 0; i < entries.length; i++) syncRail(entries[i].target);
  }) : null;

  function syncRail(el) {
    if (!el.hasAttribute('data-lokt-rail-tab')) el.setAttribute('data-lokt-rail-tab', el.getAttribute('tabindex') || '');
    if (el.getAttribute('data-lokt-rail-tab') !== '0') return;
    var cs = getComputedStyle(el);
    var scrollsX = /(auto|scroll)/.test(cs.overflowX) && el.scrollWidth > el.clientWidth + 1;
    var scrollsY = /(auto|scroll)/.test(cs.overflowY) && el.scrollHeight > el.clientHeight + 1;
    if (scrollsX || scrollsY) {
      if (el.getAttribute('tabindex') !== '0') el.setAttribute('tabindex', '0');
    } else if (el.hasAttribute('tabindex') && document.activeElement !== el) {
      el.removeAttribute('tabindex');
    }
  }

  function watchRails() {
    var rails = document.querySelectorAll(RAIL_SEL);
    for (var i = 0; i < rails.length; i++) {
      if (rails[i].hasAttribute('data-lokt-rail-tab')) continue;
      syncRail(rails[i]);
      if (railRO) railRO.observe(rails[i]);
    }
  }

  /* ---------- wiring ---------- */
  var sweepTimer = 0;
  function queueSweep() {
    if (sweepTimer) return;
    sweepTimer = setTimeout(function () {
      sweepTimer = 0;
      try { sweepAria(); } catch (err) {}
      try { watchRails(); } catch (err) {}
    }, 200);
  }

  function init() {
    try { watchSticky(); } catch (err) {}
    try { document.addEventListener('focusin', unobscure); } catch (err) {}
    try { document.addEventListener('keydown', trapDrawer, true); } catch (err) {}
    try { sweepAria(); } catch (err) {}
    try { watchRails(); } catch (err) {}
    if (window.MutationObserver) {
      new MutationObserver(function (records) {
        for (var i = 0; i < records.length; i++) {
          if (records[i].addedNodes.length || records[i].removedNodes.length) { queueSweep(); return; }
        }
      }).observe(document.body, { childList: true, subtree: true });
    }
  }

  window.LoktA11y = { measure: measure, edge: edge, sweepAria: sweepAria, watchRails: watchRails };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();

/* Keep the menu state and opening focus in sync with the actual details element. */
(function () {
  'use strict';
  function bindMenuState() {
    var details = document.getElementById('Details-menu-drawer-container');
    var drawer = document.getElementById('menu-drawer');
    if (!details || !drawer || details.hasAttribute('data-lokt-focus-bound')) return;
    var summary = details.querySelector(':scope > summary');
    var close = drawer.querySelector('.menu-drawer__close-btn');
    if (!summary || !close) return;
    details.setAttribute('data-lokt-focus-bound', '');
    var timer;
    function sync() {
      summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
      clearTimeout(timer);
      if (details.open) {
        /* The vendor focuses the covered Menu control after its transition. */
        timer = setTimeout(function () {
          if (!details.open || drawer.contains(document.activeElement)) return;
          if (document.activeElement !== summary && document.activeElement !== document.body) return;
          if (getComputedStyle(drawer).visibility === 'visible') close.focus();
        }, 350);
      } else if (drawer.contains(document.activeElement)) {
        summary.focus();
      }
    }
    details.addEventListener('toggle', sync);
    sync();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bindMenuState);
  else bindMenuState();
  document.addEventListener('shopify:section:load', bindMenuState);
})();
